// ----------------------------------------------------------------------------
// Task Template builder — defines a kind of work and its capture fields.
// A field can optionally carry a per-block monthly TARGET (estimate) — ticked with T
// here, and the values are entered in the separate Monthly Targets dialog (bullseye
// button on the template card). Captured actuals (Daily Logs) are compared against it.
// ----------------------------------------------------------------------------

function slugifyKey(label) {
    return (label || '').trim().replace(/\s+/g, '_').toLowerCase().replace(/[^a-z0-9_]/g, '');
}

// Collect the capture-field schema from the builder rows.
// Returns null if a field is missing its label.
function collectCaptureFields() {
    const fields = [];
    const usedKeys = new Set();
    let valid = true;
    groupRowsBySection();
    $('#capture-fields-list .capture-field-item').each(function () {
        const $row = $(this);
        const label = $row.find('.cf-label').val().trim();
        if (!label) { valid = false; return false; }
        // Key is an immutable identity: reuse the row's existing key (set on create/prefill);
        // only derive from the label for brand-new rows. This keeps historical metrics,
        // formulas and per-block targets joined when a label is later renamed.
        let key = ($row.attr('data-key') || '').trim();
        if (!key) key = slugifyKey(label);
        if (!key) key = 'field_' + (usedKeys.size + 1);   // symbol-only label fallback
        let uniq = key, n = 2;
        while (usedKeys.has(uniq)) { uniq = key + '_' + n; n++; }   // collision suffix
        key = uniq;
        usedKeys.add(key);
        $row.attr('data-key', key);   // persist the resolved key back onto the row
        const type = $row.find('.cf-type').val();
        if (type === 'computed' && !$row.find('.cf-formula').val().trim()) {
            Toast.show(`Give the computed field "${label}" a formula.`, "error", 3000);
            valid = null; return false;
        }
        fields.push({
            section: $row.find('.cf-section').val().trim(),
            key: key,
            label: label,
            type: type,
            unit: $row.find('.cf-unit').val().trim(),
            has_target: $row.find('.cf-target').is(':checked'),
            formula: type === 'computed' ? $row.find('.cf-formula').val().trim() : '',
        });
    });
    if (valid === null) return null;   // message already shown
    if (!valid) {
        Toast.show("Please give every capture field a label.", "error", 3000);
        return null;
    }
    return fields;
}

// ----------------------------------------------------------------------------
// Create template
// ----------------------------------------------------------------------------
$('.create-template-btn').on('click', function () {
    $.ajax({ url: '/api/task/template/add', method: 'GET' }).done(function (response) {
        const dialog = new Dialog("Create Task Template", response.html, { size: '3xl' });
        dialog.setButtons([
            { name: "Cancel", dismiss: true },
            {
                name: "Add Template",
                class: "bg-green-600 text-white hover:bg-green-700",
                onClick: function () {
                    const btn = $(this);
                    const loadingBtn = new LoadingButton(btn);
                    loadingBtn.start();
                    const csrftoken = $("[name=csrfmiddlewaretoken]").val();
                    const name = $('.t-name').val();
                    const description = $('.t-description').val();

                    if (!name) {
                        Toast.show("Template name is required.", "error", 3000);
                        loadingBtn.stop();
                        return;
                    }

                    const captureFields = collectCaptureFields();
                    if (captureFields === null) { loadingBtn.stop(); return; }

                    $.ajax({
                        url: '/api/task/template/create',
                        method: 'POST',
                        headers: { 'X-CSRFToken': csrftoken },
                        data: {
                            name: name,
                            description: description,
                            track_costs: $('#trackCosts').is(':checked'),
                            capture_fields: JSON.stringify(captureFields),
                        }
                    }).done(function () {
                        Toast.show("Task template created successfully.", "success", 3000);
                        dialog.hide();
                        location.reload();
                    }).fail(function (error) {
                        Toast.show(error.responseJSON.error, "error", 3000);
                    }).always(function () {
                        loadingBtn.stop();
                    });
                }
            }
        ]);
        dialog.show();
        initCaptureFieldSort();
    }).fail(function (error) {
        Toast.show(error.responseJSON.error, "error", 3000);
    });
});

// ----------------------------------------------------------------------------
// Edit template
// ----------------------------------------------------------------------------
$('.edit-template').on('click', function () {
    const templateId = $(this).data('id');
    if (!templateId) {
        Toast.show("Template ID not found.", "error", 3000);
        return;
    }
    $.ajax({ url: '/api/task/template/update', method: 'GET', data: { id: templateId } }).done(function (response) {
        const dialog = new Dialog("Edit Task Template", response.html, { size: '3xl' });
        dialog.setButtons([
            { name: "Cancel", dismiss: true },
            {
                name: "Update Template",
                class: "bg-green-600 text-white hover:bg-green-700 update-template-btn",
                onClick: function () {
                    const btn = $(this);
                    const loadingBtn = new LoadingButton(btn);
                    loadingBtn.start();
                    const csrftoken = $("[name=csrfmiddlewaretoken]").val();
                    const name = $('.t-name').val();
                    const description = $('.t-description').val();

                    if (!name) {
                        Toast.show("Template name is required.", "error", 3000);
                        loadingBtn.stop();
                        return;
                    }
                    const captureFields = collectCaptureFields();
                    if (captureFields === null) { loadingBtn.stop(); return; }

                    // Targets are NOT sent here — they live in the Monthly Targets dialog, so
                    // editing fields can never overwrite or clear saved targets.
                    $.ajax({
                        url: '/api/task/template/update',
                        method: 'POST',
                        headers: { 'X-CSRFToken': csrftoken },
                        data: {
                            id: templateId,
                            name: name,
                            description: description,
                            track_costs: $('#trackCosts').is(':checked'),
                            capture_fields: JSON.stringify(captureFields),
                        }
                    }).done(function () {
                        Toast.show("Task template updated successfully.", "success", 3000);
                        location.reload();
                    }).fail(function (error) {
                        Toast.show(error.responseJSON.error, "error", 3000);
                    }).always(function () {
                        loadingBtn.stop();
                        dialog.hide();
                    });
                }
            }
        ]);
        dialog.show();
        $('#trackCosts').prop('checked', response.track_costs !== false);
        // Prefill the builder from the existing template.
        prefillCaptureFields(response.capture_fields || []);
    }).fail(function (error) {
        Toast.show(error.responseJSON.error, "error", 3000);
    });
});

// ----------------------------------------------------------------------------
// Delete template
// ----------------------------------------------------------------------------
$('.delete-template').on('click', function () {
    const templateId = $(this).data('id');
    if (!templateId) {
        Toast.show("Template ID not found.", "error", 3000);
        return;
    }
    const dialog = new Dialog("Confirm Deletion",
        `<p>Are you sure you want to delete this task template? This action cannot be undone.</p>`, { size: 'md' });
    dialog.setButtons([
        { name: "Cancel", dismiss: true },
        {
            name: "Delete",
            class: "bg-red-600 text-white hover:bg-red-700 delete-template-btn",
            onClick: function () {
                const btn = $(this);
                const loadingBtn = new LoadingButton(btn);
                loadingBtn.start();
                const csrftoken = $("[name=csrfmiddlewaretoken]").val();
                $.ajax({
                    url: '/api/task/template/delete',
                    method: 'POST',
                    headers: { 'X-CSRFToken': csrftoken },
                    data: { id: templateId }
                }).done(function () {
                    Toast.show("Task template deleted successfully.", "success", 3000);
                    location.reload();
                }).fail(function (error) {
                    Toast.show(error.responseJSON.error, "error", 3000);
                }).always(function () {
                    loadingBtn.stop();
                    dialog.hide();
                });
            }
        }
    ]);
    dialog.show();
});

// ----------------------------------------------------------------------------
// Capture-field row management
// ----------------------------------------------------------------------------
function captureFieldRowHtml(fieldId, f) {
    f = f || {};
    // 'workers' → the Daily Log renders this field as a picker of the task's assigned team
    // (for crew operations like Manuring / Spraying / Census); the entry is block-attributed.
    // 'computed' → read-only in the Daily Log, worked out from its formula (e.g. Harvesting's
    // Total Bunch Count / Total Weight). It MUST be offered here, otherwise saving the template
    // silently turns a computed field back into a plain number and drops its formula.
    const types = ['number', 'time', 'text', 'workers', 'computed'];
    const opts = types.map(t =>
        `<option value="${t}"${(f.type || 'number') === t ? ' selected' : ''}>${t.charAt(0).toUpperCase() + t.slice(1)}</option>`
    ).join('');
    const isComputed = f.type === 'computed';
    const esc = v => String(v || '').replace(/"/g, '&quot;');
    return `
    <div class="capture-field-item bg-white border border-slate-300 rounded-md p-3" data-field-id="${fieldId}" data-key="${(f.key || '').replace(/"/g, '&quot;')}">
      <div class="flex items-center gap-2">
        <span class="cf-handle w-4 shrink-0 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing" title="Drag to reorder within the section">
          <i class="bi bi-grip-vertical"></i>
        </span>
        <div class="grid grid-cols-12 gap-2 items-center flex-1">
          <input type="text" class="cf-section col-span-3 border border-slate-300 rounded-md px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Section" value="${f.section || ''}">
          <input type="text" class="cf-label col-span-3 border border-slate-300 rounded-md px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Field label *" data-field-id="${fieldId}" value="${(f.label || '').replace(/"/g, '&quot;')}">
          <select class="cf-type col-span-2 border border-slate-300 rounded-md px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            ${opts}
          </select>
          <input type="text" class="cf-unit col-span-2 border border-slate-300 rounded-md px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Unit" value="${f.unit || ''}">
          <label class="col-span-1 inline-flex items-center justify-center gap-1 text-xs text-slate-600" title="Set a monthly target for this field">
            <input type="checkbox" class="cf-target" data-field-id="${fieldId}"${f.has_target ? ' checked' : ''}> T
          </label>
          <button type="button" class="remove-capture-field col-span-1 text-red-500 hover:text-red-700 text-center" data-field-id="${fieldId}">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
      <div class="cf-formula-row mt-2 ml-6 flex items-center gap-2${isComputed ? '' : ' hidden'}">
        <span class="text-xs font-medium text-slate-500 whitespace-nowrap"><i class="bi bi-calculator"></i> Formula</span>
        <input type="text" class="cf-formula flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-xs font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. ripe_bunch_count + black_bunch_count   (field keys, + - * / and brackets; abw = block's previous-month ABW)"
          value="${esc(f.formula)}">
      </div>
    </div>`;
}

// Show the formula box only for Computed fields.
$(document).on('change', '.cf-type', function () {
    $(this).closest('.capture-field-item').find('.cf-formula-row').toggleClass('hidden', $(this).val() !== 'computed');
});

$(document).on('click', '#add-capture-field-btn', function () {
    const fieldId = 'field_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
    $('#no-capture-fields').hide();
    $('#capture-fields-list').append(captureFieldRowHtml(fieldId, {}));
});

$(document).on('click', '.remove-capture-field', function () {
    const fieldId = $(this).data('field-id');
    $(`.capture-field-item[data-field-id="${fieldId}"]`).remove();
    if ($('#capture-fields-list .capture-field-item').length === 0) $('#no-capture-fields').show();
});

// ----------------------------------------------------------------------------
// Section grouping + drag-and-drop order. Fields are kept together by section
// (matched case-insensitively, ignoring surrounding spaces); the handle drags a
// field only among the fields of its own section.
// ----------------------------------------------------------------------------
function cfSectionOf(row) {
    return ($(row).find('.cf-section').val() || '').trim().toLowerCase();
}

// Move a row to the end of its section, when another row already has that section.
function placeRowInSection($row) {
    const section = cfSectionOf($row);
    const $same = $('#capture-fields-list .capture-field-item').not($row)
        .filter(function () { return cfSectionOf(this) === section; });
    if ($same.length) $same.last().after($row);
}

// Regroup every row by section (sections keep their first-seen order; fields keep
// their order inside the section), so the saved order always matches the Daily Log.
function groupRowsBySection() {
    const $list = $('#capture-fields-list');
    const groups = {}, order = [];
    $list.find('.capture-field-item').each(function () {
        const s = cfSectionOf(this);
        if (!(s in groups)) { groups[s] = []; order.push(s); }
        groups[s].push(this);
    });
    order.forEach(function (s) { groups[s].forEach(function (row) { $list.append(row); }); });
}

function initCaptureFieldSort() {
    const list = document.getElementById('capture-fields-list');
    if (!list || typeof Sortable === 'undefined') return;
    if (list._cfSortable) list._cfSortable.destroy();
    list._cfSortable = Sortable.create(list, {
        handle: '.cf-handle',
        draggable: '.capture-field-item',
        animation: 150,
        ghostClass: 'opacity-40',
        // Only allow drops beside fields of the same section.
        onMove: function (evt) { return cfSectionOf(evt.dragged) === cfSectionOf(evt.related); },
    });
}

$(document).on('change', '#capture-fields-list .cf-section', function () {
    placeRowInSection($(this).closest('.capture-field-item'));
});

// ----------------------------------------------------------------------------
// Prefill (edit) — rebuild capture rows from a saved template.
// ----------------------------------------------------------------------------
function prefillCaptureFields(captureFields) {
    $('#capture-fields-list .capture-field-item').remove();
    initCaptureFieldSort();
    if (!captureFields.length) { $('#no-capture-fields').show(); return; }
    $('#no-capture-fields').hide();
    captureFields.forEach(function (f) {
        const fieldId = 'field_' + Date.now() + '_' + Math.floor(Math.random() * 100000);
        $('#capture-fields-list').append(captureFieldRowHtml(fieldId, f));
    });
}

// ----------------------------------------------------------------------------
// Monthly Targets (and, for Harvesting, ABW) per Block — its own dialog (target button on each template card).
// Values are kept in memory per block/field/year while the dialog is open, so switching
// block or year never loses edits; Save sends only the block/field/year combinations
// that were touched, and each is replaced exactly on the server.
// ----------------------------------------------------------------------------
$('.template-targets').on('click', function () {
    const templateId = $(this).data('id');
    if (!templateId) { Toast.show("Template ID not found.", "error", 3000); return; }
    $.ajax({ url: '/api/task/template/targets', method: 'GET', data: { id: templateId } }).done(function (response) {
        const fields = response.fields || [];
        // values[blockId][key][year][month] = number (seeded from the saved targets)
        const values = {};
        Object.keys(response.block_measurements || {}).forEach(function (blockId) {
            const byKey = response.block_measurements[blockId] || {};
            Object.keys(byKey).forEach(function (key) {
                (byKey[key] || []).forEach(function (t) {
                    const y = parseInt(t.year), m = parseInt(t.month), v = parseFloat(t.target_value);
                    if (!y || !m || !(v > 0)) return;
                    ((values[blockId] = values[blockId] || {})[key] = values[blockId][key] || {});
                    (values[blockId][key][y] = values[blockId][key][y] || {})[m] = v;
                });
            });
        });
        const touched = {};   // "blockId|key|year" -> true

        // Harvesting's dialog holds two things (targets + ABW), so it is titled for both.
        const title = response.has_abw ? "Monthly Targets & ABW per Block" : "Monthly Targets per Block";
        const dialog = new Dialog(title, response.html, { size: '3xl' });
        const buttons = [{ name: fields.length ? "Cancel" : "Close", dismiss: true }];
        if (fields.length) buttons.push({
            name: response.has_abw ? "Save" : "Save Targets",
            class: "bg-green-600 text-white hover:bg-green-700",
            onClick: function () {
                const loadingBtn = new LoadingButton($(this));
                loadingBtn.start();
                const payload = {};
                Object.keys(touched).forEach(function (t) {
                    const [blockId, key, year] = t.split('|');
                    const months = ((values[blockId] || {})[key] || {})[year] || {};
                    ((payload[blockId] = payload[blockId] || {})[key] = payload[blockId][key] || {})[year] = months;
                });
                $.ajax({
                    url: '/api/task/template/targets',
                    method: 'POST',
                    headers: { 'X-CSRFToken': $("[name=csrfmiddlewaretoken]").val() },
                    data: { id: templateId, targets: JSON.stringify(payload) }
                }).done(function () {
                    Toast.show("Monthly targets saved.", "success", 3000);
                    dialog.hide();
                }).fail(function (error) {
                    Toast.show((error.responseJSON && error.responseJSON.error) || "Failed to save targets.", "error", 3000);
                }).always(function () { loadingBtn.stop(); });
            }
        });
        dialog.setButtons(buttons);
        dialog.show();

        const $root = $('#tplTargets');
        $root.find('#ttYear').val(new Date().getFullYear());

        // Show the selected year's saved/edited values in every block's month grids.
        function fillYear() {
            const year = parseInt($root.find('#ttYear').val());
            $root.find('.tt-block').each(function () {
                const blockId = String($(this).data('block-id'));
                $(this).find('.tt-field').each(function () {
                    const key = String($(this).data('key'));
                    const months = (((values[blockId] || {})[key] || {})[year]) || {};
                    $(this).find('.tt-month').each(function () {
                        const v = months[parseInt($(this).data('month'))];
                        $(this).val(v ? v : '');
                    });
                });
            });
        }
        function setValue($input) {
            const blockId = String($input.closest('.tt-block').data('block-id'));
            const key = String($input.closest('.tt-field').data('key'));
            const year = parseInt($root.find('#ttYear').val());
            const month = parseInt($input.data('month'));
            const v = parseFloat($input.val());
            const byYear = (((values[blockId] = values[blockId] || {})[key] = values[blockId][key] || {})[year] = values[blockId][key][year] || {});
            if (v > 0) byYear[month] = v; else delete byYear[month];
            touched[blockId + '|' + key + '|' + year] = true;
        }
        fillYear();

        $root.on('input', '.tt-month', function () { setValue($(this)); });
        $root.on('click', '.tt-clear', function () {
            $(this).closest('.tt-field').find('.tt-month').each(function () { $(this).val(''); setValue($(this)); });
        });
        $root.on('click', '.tt-year-btn', function () {
            const year = (parseInt($root.find('#ttYear').val()) || new Date().getFullYear()) + parseInt($(this).data('dir'));
            if (year < 2020 || year > 2099) return;
            $root.find('#ttYear').val(year);
            fillYear();
        });
        $root.on('change', '#ttBlock', function () {
            const id = String($(this).val());
            $root.find('.tt-block').each(function () { $(this).toggleClass('hidden', String($(this).data('block-id')) !== id); });
        });
    }).fail(function (error) {
        Toast.show((error.responseJSON && error.responseJSON.error) || "Failed to load targets.", "error", 3000);
    });
});
